import axios from 'axios';
import { DIRECTUS_INSTANCE, ACCOUNT_API_BASE } from '../../api';

// The WordPress plugin sends people to /signin?wp_connect=1&callback=…&state=….
// Someone without an account goes on to /signup and /onboarding, so the handshake
// is kept in storage until it is sent back. The plugin's state lives for an hour.
//
// Plugins that send connect_flow=code get a one-time code in the callback URL and
// swap it server-to-server for the workspace token, so the token never lands in
// WordPress access logs or browser history. Older plugin releases still expect
// workspace_token in the URL; that legacy path stays for one release only.
const STORAGE_KEY = 'prosecaptain_wp_connect';
const MAX_AGE_MS = 60 * 60 * 1000;

export const WP_CONNECT_STAGE_LABELS = {
  idle: 'Przygotowanie połączenia',
  loading_user: 'Sprawdzanie konta użytkownika',
  loading_workspace: 'Łączenie z workspace',
  loading_assistant: 'Pobieranie konfiguracji asystenta',
  redirecting: 'Finalizowanie i przekierowanie'
};

const isAllowedWpCallback = (callbackUrl) => {
  try {
    const parsed = new URL(callbackUrl);
    const isHttp = parsed.protocol === 'https:' || parsed.protocol === 'http:';
    const hasExpectedPath = parsed.pathname.endsWith('/wp-admin/admin-post.php');
    const hasExpectedAction = parsed.searchParams.get('action') === 'prosecaptain_connect_callback';
    return isHttp && hasExpectedPath && hasExpectedAction;
  } catch (_) {
    return false;
  }
};

export const clearPendingWpConnect = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // ignore storage errors
  }
};

export const getPendingWpConnect = () => {
  try {
    const pending = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!pending || !isAllowedWpCallback(pending.callback) || !pending.state) return null;
    if (Date.now() - Number(pending.startedAt || 0) > MAX_AGE_MS) {
      clearPendingWpConnect();
      return null;
    }
    return pending;
  } catch (_) {
    return null;
  }
};

/** Stores the handshake from the URL, or returns the one already in progress. */
export const captureWpConnect = (searchParams) => {
  if (searchParams.get('wp_connect') !== '1') return getPendingWpConnect();

  const callback = searchParams.get('callback');
  const state = searchParams.get('state');
  if (!callback || !state || !isAllowedWpCallback(callback)) return null;

  const pending = {
    callback,
    state,
    flow: searchParams.get('connect_flow') === 'code' ? 'code' : 'legacy',
    wpEndpoint: searchParams.get('wp_endpoint') || '',
    wpIntegrationToken: searchParams.get('wp_integration_token') || '',
    startedAt: Date.now()
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch (_) {
    // the flow still works for someone who already has a workspace
  }
  return pending;
};

/** The plugin's settings screen, for going back to WordPress without connecting. */
export const getWpSettingsUrl = (pending) => {
  try {
    const url = new URL(pending.callback);
    url.pathname = url.pathname.replace(/admin-post\.php$/, 'admin.php');
    url.search = '';
    url.searchParams.set('page', 'prosecaptain-settings');
    return url.toString();
  } catch (_) {
    return '';
  }
};

export const getWpSiteHost = (pending) => {
  try {
    return new URL(pending.callback).host;
  } catch (_) {
    return '';
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Asks the API for a one-time code bound to this plugin's state and callback.
 * 409 means the workspace (or its token) is not there yet, e.g. right after
 * onboarding, so it is retried like the legacy token lookup.
 */
export const requestWpConnectCode = async (authToken, pending, attempts = 1) => {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const response = await axios.post(
        `${ACCOUNT_API_BASE}/wp-connect/code`,
        { callback: pending.callback, state: pending.state },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const code = response.data?.code || '';
      if (code) return code;
    } catch (error) {
      if (error?.response?.status !== 409) throw error;
    }
    if (attempt < attempts - 1) await sleep(1000);
  }
  return '';
};

const fetchWorkspaceToken = async (authToken, workspaceId, attempts) => {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const response = await axios.get(`${DIRECTUS_INSTANCE}/items/workspace/${workspaceId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
      params: { fields: ['widget_token'] }
    });
    const token = response.data?.data?.widget_token || '';
    if (token) return token;
    if (attempt < attempts - 1) await sleep(1000);
  }
  return '';
};

/**
 * Collects the workspace credentials and sends the browser back to WordPress.
 * Resolves to 'redirected', or to 'needs_onboarding' when the account has no
 * workspace yet: the handshake then stays pending until onboarding finishes.
 */
export const completeWpConnect = async (authToken, pending, options = {}) => {
  const onStageChange = typeof options.onStageChange === 'function' ? options.onStageChange : () => {};
  const tokenAttempts = options.tokenAttempts || 1;

  try {
    onStageChange('loading_user');
    const meResponse = await axios.get(`${DIRECTUS_INSTANCE}/users/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
      params: { fields: ['linked_workspace.id', 'onboarding_completed'] }
    });
    const me = meResponse.data?.data || {};
    const workspaceId = me.linked_workspace?.id;
    if (!workspaceId || !me.onboarding_completed) {
      onStageChange('idle');
      return 'needs_onboarding';
    }

    onStageChange('loading_workspace');
    const isCodeFlow = pending.flow === 'code';
    const connectCode = isCodeFlow ? await requestWpConnectCode(authToken, pending, tokenAttempts) : '';
    // Deprecated: only for plugin releases that predate the code exchange.
    const workspaceToken = isCodeFlow ? '' : await fetchWorkspaceToken(authToken, workspaceId, tokenAttempts);
    if (isCodeFlow ? !connectCode : !workspaceToken) {
      throw new Error('workspace_token_not_found');
    }

    let assistantStatus = 'draft';
    let assistantWidgetId = '';
    let brandColor = '';
    try {
      onStageChange('loading_assistant');
      const assistantResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/widget_config`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          filter: JSON.stringify({ widget_linked_workspace: { _eq: workspaceId } }),
          sort: '-date_updated',
          limit: 1,
          fields: ['id', 'status', 'widget_color']
        }
      });
      const firstWidget = assistantResponse.data?.data?.[0];
      if (firstWidget) {
        assistantStatus = firstWidget.status || 'draft';
        assistantWidgetId = firstWidget.id || '';
        brandColor = firstWidget.widget_color || '';
      }
    } catch (_) {
      // Non-critical for callback.
    }

    const cbUrl = new URL(pending.callback);
    cbUrl.searchParams.set('state', pending.state);
    if (isCodeFlow) {
      cbUrl.searchParams.set('code', connectCode);
    } else {
      cbUrl.searchParams.set('workspace_id', String(workspaceId));
      cbUrl.searchParams.set('workspace_token', String(workspaceToken));
    }
    cbUrl.searchParams.set('assistant_status', String(assistantStatus));
    if (assistantWidgetId) cbUrl.searchParams.set('assistant_widget_id', String(assistantWidgetId));
    if (brandColor) cbUrl.searchParams.set('brand_color', String(brandColor));
    if (pending.wpEndpoint) cbUrl.searchParams.set('wp_endpoint', pending.wpEndpoint);
    if (pending.wpIntegrationToken) cbUrl.searchParams.set('wp_integration_token', pending.wpIntegrationToken);

    onStageChange('redirecting');
    clearPendingWpConnect();
    window.location.href = cbUrl.toString();
    return 'redirected';
  } catch (_) {
    const cbUrl = new URL(pending.callback);
    cbUrl.searchParams.set('state', pending.state);
    cbUrl.searchParams.set('error', 'connect_payload_failed');
    clearPendingWpConnect();
    window.location.href = cbUrl.toString();
    return 'redirected';
  }
};
